import { FC } from "react";
import {
  OakGrid,
  OakGridArea,
  OakHeading,
  OakP,
  OakSpan,
  OakFlex,
  OakBox,
  OakTertiaryButton,
  OakTagFunctional,
} from "@oaknational/oak-components";
import styled from "styled-components";

import { LessonOverviewDownloadAndShareButtons } from "../LessonOverviewDownloadAndShareButtons/LessonOverviewDownloadAndShareButtons";

import { LessonOverviewHeaderProps } from "@/components/TeacherComponents/LessonOverviewHeader";
import SubjectIconBrushBorders from "@/components/TeacherComponents/SubjectIconBrushBorders";
import ComplexCopyrightRestrictionBanner from "@/components/TeacherComponents/ComplexCopyrightRestrictionBanner/ComplexCopyrightRestrictionBanner";
import LessonMetadata from "@/components/SharedComponents/LessonMetadata";

const CustomDimensionRow = styled(OakFlex)`
  width: 200px;
  @media (max-width: 1280px) {
    width: 172px;
  }
`;
const CustomDimensionSquare = styled(CustomDimensionRow)`
  height: 200px;
  @media (max-width: 1280px) {
    height: 172px;
  }
`;

export const LessonOverviewHeaderDesktop: FC<
  LessonOverviewHeaderProps & { shareButtons: React.ReactNode }
> = (props) => {
  const {
    subjectSlug,
    yearTitle,
    tierTitle,
    examBoardTitle,
    lessonTitle,
    isNew,
    subjectIconBackgroundColor,
    pupilLessonOutcome,
    isCanonical,
    phonicsOutcome,
    orderInUnit,
    unitTotalLessonCount,
    breadcrumbs,
    programmeSlug,
    unitTitle,
    geoRestricted,
    loginRequired,
    shareButtons,
  } = props;

  const previousBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
  const shouldShowBackButton =
    !!previousBreadcrumb && !!unitTitle && !!programmeSlug && !isCanonical;

  return (
    <OakBox $display={["none", "grid"]}>
      <OakGrid>
        <OakGridArea $justifyContent={"center"} $colSpan={[12, 3]}>
          <OakFlex
            $flexDirection={"column"}
            $alignItems={"flex-start"}
            $gap={"spacing-32"}
          >
            {shouldShowBackButton && (
              <CustomDimensionRow data-testid={"back-button-row"}>
                <OakTertiaryButton
                  element="a"
                  href={previousBreadcrumb.href}
                  iconName="arrow-left"
                >
                  View unit
                </OakTertiaryButton>
              </CustomDimensionRow>
            )}
            <CustomDimensionSquare>
              <SubjectIconBrushBorders
                subjectSlug={subjectSlug}
                color={subjectIconBackgroundColor}
                isNew={isNew}
              />
            </CustomDimensionSquare>
          </OakFlex>
        </OakGridArea>
        <OakGridArea
          $justifyContent={"flex-start"}
          $colSpan={[12, 9]}
          $alignItems={"flex-start"}
        >
          <OakFlex $flexDirection={"column"} $gap="spacing-8">
            {shouldShowBackButton && (
              <OakTagFunctional
                data-testid={"lesson-count-tag"}
                label={`Lesson ${orderInUnit} of ${unitTotalLessonCount}`}
                $background={"bg-decorative4-main"}
                $mb={"spacing-16"}
                $width={"fit-content"}
                useSpan={true}
              />
            )}
            {(examBoardTitle || yearTitle || tierTitle) && !isCanonical && (
              <OakSpan $color={"text-subdued"} $font={"heading-light-7"}>
                <LessonMetadata
                  examBoardTitle={examBoardTitle}
                  yearTitle={yearTitle}
                  tierTitle={!isCanonical ? tierTitle : null}
                />
              </OakSpan>
            )}

            <OakFlex $flexDirection={"column"} $gap="spacing-24">
              <OakHeading tag={"h1"} $font={"heading-3"}>
                {lessonTitle}
              </OakHeading>
              <OakBox>
                {phonicsOutcome && (
                  <OakP $font={"body-2-bold"}>Learning outcomes</OakP>
                )}
                <OakBox $maxWidth={"spacing-960"}>
                  {pupilLessonOutcome && (
                    <OakP $font={"body-2"}>{pupilLessonOutcome}</OakP>
                  )}
                  {phonicsOutcome && (
                    <OakP $font={"body-2"}>{phonicsOutcome}</OakP>
                  )}
                </OakBox>
              </OakBox>
              <LessonOverviewDownloadAndShareButtons
                {...props}
                shareButtons={shareButtons}
              />
              <ComplexCopyrightRestrictionBanner
                isGeorestricted={geoRestricted}
                isLoginRequired={loginRequired}
                componentType="lesson_overview"
              />
            </OakFlex>
          </OakFlex>
        </OakGridArea>
      </OakGrid>
    </OakBox>
  );
};
